/**
 * Branding hook — resuelve el partner/afiliado activo desde:
 *   1) query param `?partner=CODE` (dev/preview)
 *   2) subdominio `nunoa.micomunidadsegura.cl`
 *   3) primer segmento del path `/CODE` (link de afiliado)
 *
 * El resultado se cachea en window para evitar llamadas repetidas al backend
 * durante la misma sesión.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hardcoded fallback for Ñuñoa (mantiene compatibilidad con la primera versión)
const HARDCODED_BRANDS = {
  nunoa: {
    key: "nunoa",
    codigo: "NUNOA",
    name: "Ñuñoa",
    cobrandeado: true,
    logoDark: "https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/mehfwtha_logo-nunoa-fondo-oscuro.png",
    banner: {
      text: "Beneficio exclusivo para edificios y condominios de Ñuñoa.",
      subtext: "Para generar ahorro a las comunidades",
      bg: "#1E3FAE",
      color: "#FFFFFF",
      titleBold: true,
      subtextBold: false,
    },
  },
};

// Slugs reservados que jamás deben interpretarse como código de afiliado
const RESERVED_PATH_SEGMENTS = new Set([
  "", "admin", "partner", "spm", "contacto", "carta-asignacion",
  "programa-administradores", "cotizar-comunidad", "cotizar-residente",
  "afiliado", "afiliados", "api", "static", "assets",
]);

const CACHE_KEY = "__mcs_brand_cache__";

function getCandidateCode() {
  if (typeof window === "undefined") return null;
  // 1) Query param (preview / testing)
  const qs = new URLSearchParams(window.location.search);
  const q = qs.get("partner");
  if (q) return q.toLowerCase();

  // 2) Subdomain
  const host = window.location.hostname.toLowerCase();
  const parts = host.split(".");
  if (parts.length >= 2) {
    const sub = parts[0];
    if (sub && sub !== "www" && sub !== "micomunidadsegura" && sub !== "cotiza-ahora") {
      return sub;
    }
  }

  // 3) First path segment (/FC, /nunoa)
  const seg = (window.location.pathname || "/").split("/")[1] || "";
  const segL = seg.toLowerCase();
  if (segL && !RESERVED_PATH_SEGMENTS.has(segL) && segL.length <= 15) {
    return segL;
  }
  return null;
}

async function resolveFromBackend(code) {
  try {
    const { data } = await axios.get(`${API}/afiliados/resolve/${encodeURIComponent(code)}`);
    return {
      key: code,
      codigo: data.codigo,
      name: data.nombre,
      cobrandeado: !!data.cobrandeado,
      logoDark: data.logo_url || null,
      logoInvertHeader: !!data.logo_invert_header,
      banner: {
        text: data.banner_titulo || "",
        subtext: data.banner_subtexto || "",
        // Color fijo para todos los afiliados (identidad visual consistente)
        bg: "#1E3FAE",
        color: "#FFFFFF",
        titleBold: data.banner_titulo_bold !== false,
        subtextBold: !!data.banner_subtexto_bold,
      },
    };
  } catch {
    return null;
  }
}

/**
 * Sync version — used inside forms where we only need the partner CODE, not the full brand.
 * Returns the candidate code even if backend hasn't validated it yet.
 */
export function getCurrentBrand() {
  if (typeof window === "undefined") return null;
  const cached = window[CACHE_KEY];
  if (cached && cached.__resolvedAt) return cached.brand;
  const code = getCandidateCode();
  if (!code) return null;
  if (HARDCODED_BRANDS[code]) return HARDCODED_BRANDS[code];
  // Fallback: just return code, no branding info
  return { key: code, codigo: code.toUpperCase(), name: code.toUpperCase(), cobrandeado: false, logoDark: null, banner: null };
}

export function useBranding() {
  const [brand, setBrand] = useState(() => {
    if (typeof window === "undefined") return null;
    if (window[CACHE_KEY]) return window[CACHE_KEY].brand;
    return null;
  });

  useEffect(() => {
    let cancelled = false;
    const cached = typeof window !== "undefined" ? window[CACHE_KEY] : null;
    if (cached && cached.__resolvedAt) {
      setBrand(cached.brand);
      return;
    }
    const code = getCandidateCode();
    if (!code) {
      if (typeof window !== "undefined") window[CACHE_KEY] = { brand: null, __resolvedAt: Date.now() };
      setBrand(null);
      return;
    }
    // Hardcoded first
    if (HARDCODED_BRANDS[code]) {
      const b = HARDCODED_BRANDS[code];
      if (typeof window !== "undefined") window[CACHE_KEY] = { brand: b, __resolvedAt: Date.now() };
      setBrand(b);
      return;
    }
    // Resolve from backend
    resolveFromBackend(code).then((b) => {
      if (cancelled) return;
      if (typeof window !== "undefined") window[CACHE_KEY] = { brand: b, __resolvedAt: Date.now() };
      setBrand(b);
    });
    return () => { cancelled = true; };
  }, []);

  return { brand, isPartner: !!brand };
}
